/**
 * @fileoverview Query-string normalizers for analytics and edge redirects.
 * @description Repairs malformed `?foo=1?bar=2` concatenations and strips params
 * that fragment GA4 page reports without changing page content (`hl`, `v3`, …).
 * Edge redirects preserve click-id params (`gclid`, `fbclid`, …) for attribution;
 * client GA4 pageviews additionally strip `_ga` / `fbclid` for report consolidation.
 * Pure string/URL logic — safe for Edge middleware and client analytics.
 *
 * @module lib/analytics/normalize-query-string
 */

/** Query params stripped at the edge (noise only — click IDs must survive). */
export const EDGE_STRIP_QUERY_PARAM_NAMES = new Set(['hl', 'v3'])

/**
 * Query params stripped in client GA4 pageviews (includes edge noise plus
 * params safe to drop after the request lands).
 */
export const CLIENT_GA4_STRIP_QUERY_PARAM_NAMES = new Set([
  'hl',
  'v3',
  '_ga',
  'fbclid',
])

/** @deprecated Prefer {@link CLIENT_GA4_STRIP_QUERY_PARAM_NAMES}. */
export const ANALYTICS_STRIP_QUERY_PARAM_NAMES = CLIENT_GA4_STRIP_QUERY_PARAM_NAMES

/** Param keys whose values may contain literal `?` (site search). */
const SEARCH_VALUE_PARAM_KEYS = new Set(['q', 's', 'query', 'search'])

/** Param keys whose values embed paths/URLs that may contain `?` (auth redirects). */
const URL_EMBEDDING_PARAM_KEYS = new Set([
  'redirect',
  'return',
  'return_to',
  'return_url',
  'returnurl',
  'redirect_uri',
  'redirect_url',
  'next',
  'url',
  'destination',
  'callback',
  'continue',
])

/**
 * Whether an inner `?` at `index` should become `&` (malformed join vs value literal).
 *
 * @param {string} raw - Search string without leading `?`.
 * @param {number} index - Index of the inner `?` in `raw`.
 * @returns {boolean} True when this `?` starts a spurious duplicate segment.
 */
function shouldRepairInnerQuestionMark(raw: string, index: number): boolean {
  const segmentStart = raw.lastIndexOf('&', index - 1) + 1
  const segment = raw.slice(segmentStart, index)
  const eqIdx = segment.indexOf('=')
  if (eqIdx === -1) return false
  const key = segment.slice(0, eqIdx).toLowerCase()
  const tail = raw.slice(index + 1)
  const nextMatch = /^([a-zA-Z0-9_-]+)=/.exec(tail)
  if (!nextMatch?.[1]) return false
  const nextKey = nextMatch[1].toLowerCase()
  const isDuplicateKey = raw.slice(0, index).includes(`${nextKey}=`)
  const isNoiseRepeat =
    EDGE_STRIP_QUERY_PARAM_NAMES.has(nextKey) ||
    nextKey.startsWith('utm_') ||
    nextKey === 'gclid' ||
    nextKey === 'fbclid'
  if (URL_EMBEDDING_PARAM_KEYS.has(key)) {
    return false
  }
  if (SEARCH_VALUE_PARAM_KEYS.has(key)) {
    return isDuplicateKey || isNoiseRepeat
  }
  return true
}

/**
 * Repairs a search string where extra segments were joined with `?` instead of `&`.
 * Only rewrites `?` when followed by a param key boundary (`key=`), preserving
 * literal `?` characters inside site-search values.
 *
 * @param {string} search - Raw search including leading `?`, or empty.
 * @returns {string} Repaired search (`?key=val` or ``).
 */
export function repairMalformedSearchString(search: string): string {
  if (!search) return ''
  const raw = search.startsWith('?') ? search.slice(1) : search
  if (!raw) return ''
  if (!raw.includes('?')) return `?${raw}`

  let repaired = ''
  let cursor = 0
  while (cursor < raw.length) {
    const qIdx = raw.indexOf('?', cursor)
    if (qIdx === -1) {
      repaired += raw.slice(cursor)
      break
    }
    repaired += raw.slice(cursor, qIdx)
    if (shouldRepairInnerQuestionMark(raw, qIdx)) {
      repaired += '&'
    } else {
      repaired += '?'
    }
    cursor = qIdx + 1
  }

  return repaired ? `?${repaired}` : ''
}

/**
 * Removes query params from a search string according to a strip set.
 *
 * @param {string} search - Search string with leading `?`, or empty.
 * @param {ReadonlySet<string>} stripNames - Lowercase param names to delete.
 * @returns {string} Filtered search (`?…` or ``).
 */
function stripQueryParamsBySet(
  search: string,
  stripNames: ReadonlySet<string>,
): string {
  if (!search) return ''
  const raw = search.startsWith('?') ? search.slice(1) : search
  if (!raw) return ''
  const params = new URLSearchParams(raw)
  let changed = false
  for (const key of [...params.keys()]) {
    if (stripNames.has(key.toLowerCase())) {
      params.delete(key)
      changed = true
    }
  }
  if (!changed) return search.startsWith('?') ? search : `?${raw}`
  const next = params.toString()
  return next ? `?${next}` : ''
}

/**
 * Removes edge-safe noise params (`hl`, `v3`) while preserving click IDs.
 *
 * @param {string} search - Search string with leading `?`, or empty.
 * @returns {string} Filtered search (`?…` or ``).
 */
export function stripEdgeNoiseQueryParams(search: string): string {
  return stripQueryParamsBySet(search, EDGE_STRIP_QUERY_PARAM_NAMES)
}

/**
 * Removes analytics-noise query params for client GA4 pageviews.
 *
 * @param {string} search - Search string with leading `?`, or empty.
 * @returns {string} Filtered search (`?…` or ``).
 */
export function stripAnalyticsNoiseQueryParams(search: string): string {
  return stripQueryParamsBySet(search, CLIENT_GA4_STRIP_QUERY_PARAM_NAMES)
}

/**
 * Normalizes a URL search string for cacheable edge redirects.
 * Repairs malformed joins and strips `hl`/`v3` only — click IDs are preserved.
 *
 * @param {string} search - Incoming search from `URL.search` (may be empty).
 * @returns {string | null} Canonical search when changed, otherwise `null`.
 */
export function normalizeEdgeQuerySearch(search: string): string | null {
  const repaired = repairMalformedSearchString(search)
  const stripped = stripEdgeNoiseQueryParams(repaired)
  if (stripped === search) return null
  return stripped
}

/**
 * Normalizes a URL search string for client analytics and authored-link audits.
 *
 * @param {string} search - Incoming search from `URL.search` (may be empty).
 * @returns {string | null} Canonical search when changed, otherwise `null`.
 */
export function normalizeQuerySearch(search: string): string | null {
  const repaired = repairMalformedSearchString(search)
  const stripped = stripAnalyticsNoiseQueryParams(repaired)
  if (stripped === search) return null
  return stripped
}
